//
//  CsvDownloadTable.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useCsvDownload } from "../hooks/useCsvDownload";
import { CsvDownloader } from "../network/type";

export type CsvDownloadTableRow = {
  fileName: string;
  fileNameShort: string;
  createdTime: string;
  progress: number;
};

interface CsvDownloadTableProps {
  files: CsvDownloadTableRow[];
  count: number;
  rowsPerPage: number;
  page: number;
  hadlePageChange: (page: number) => void;
  /** ダウンロードAPIの差し替え用（省略時は従来の /csv-download） */
  downloader?: CsvDownloader;
}

const TABLE_SIZE = 650;
const BACKGROUND_COLOR = "#b7b7b9db";

const CsvDownloadTable: React.FC<CsvDownloadTableProps> = ({
  files,
  count,
  rowsPerPage,
  page,
  hadlePageChange,
  downloader,
}) => {
  const { handleCsvDownload, isDownloading } = useCsvDownload(downloader);

  return (
    <Box sx={{ position: "relative" }}>
      {isDownloading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{ maxWidth: TABLE_SIZE, border: 1, borderColor: "gray" }}
      >
        <Table
          sx={{
            minWidth: TABLE_SIZE,
            maxWidth: TABLE_SIZE,
            margin: "auto",
          }}
          size="small"
          aria-label="a dense table"
        >
          <TableHead sx={{ backgroundColor: BACKGROUND_COLOR }}>
            <TableRow>
              <TableCell align="center">ファイル名</TableCell>
              <TableCell align="center">受付日時</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file: CsvDownloadTableRow) => {
              const isReady = file.progress >= 1.0;
              const isDisabled = isDownloading || !isReady;
              return (
                <TableRow key={file.fileName}>
                  <TableCell
                    align="center"
                    style={{ cursor: isDisabled ? "default" : "pointer" }}
                  >
                    <a
                      style={{
                        textDecoration: isDisabled ? "none" : "underline",
                        color: isDisabled ? "gray" : "#1976d2",
                        pointerEvents: isDisabled ? "none" : "auto",
                      }}
                      onClick={
                        isDisabled
                          ? undefined
                          : () => handleCsvDownload(file.fileName)
                      }
                    >
                      {file.fileNameShort}
                    </a>
                  </TableCell>
                  <TableCell align="center">
                    {file.progress < 1.0 ? (
                      <Box sx={{ width: "100%" }}>
                        <LinearProgress
                          variant="determinate"
                          value={file.progress * 100}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {`${Math.round(file.progress * 100)}%`}
                        </Typography>
                      </Box>
                    ) : (
                      file.createdTime
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={count}
          rowsPerPageOptions={[]}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => {
            hadlePageChange(newPage);
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default CsvDownloadTable;
